import { defineStore } from 'pinia';
import { useI18n } from 'vue-i18n';
import { IdentityProvider } from '~~/data/enums/IdentityProvider';
import { Role } from '~~/data/enums/Role';
import { User } from '~~/data/resources/User';

// TODO: Look at this properly, and combine all Role utilities into a helper
export const roles = [
  {
    name: 'User',
    value: 0,
  },
  {
    name: 'Viewer',
    value: 1,
  },
  {
    name: 'Author',
    value: 2,
  },
  {
    name: 'Employer Admin',
    value: 3,
  },
  // {
  //   name: 'Triple Admin',
  //   value: 4,
  // },
];

export const useUser = defineStore('user', {
  state: (): Partial<User> => ({
    principalId: null,
    id: null,
    email: null,
    name: null,
    role: null,
    rolePermissions: null,
    employerId: null,
    employerName: null,
    acceptedPrivacyPolicy: null,
    acceptedTermsAndConditions: null,
    locale: null,
    hasPA: false,
    hasPE: false,
    identityProviderType: IdentityProvider.UNKNOWN
  }),

  getters: {
    isSet: (state) => !!state.id,
    username: (state) => state.name,
    initials: (state) => {
      if (state.name != null) {
        const matches = state.name
          ?.match(/(^\S\S?|\s\S)?/g)
          ?.map((v) => v.trim())
          ?.join('')
          ?.match(/(^\S|\S$)?/g);
        return matches.join('').toLocaleUpperCase() || '-';
      }
      return '';
    },
    isTripleAdmin: (state) => state.role === Role.TripleAdmin,
    isEmployerAdmin: (state) => state.role === Role.EmployerAdmin,
    isReadonlyAdmin: (state) => state.role === Role.TripleaReadonlyAdmin,
    isEmployerAdminOrViewer: (state) =>
      state.role === Role.EmployerAdmin || state.role === Role.Viewer || state.role === Role.TripleaReadonlyAdmin,
    isAuthor: (state) => state.role === Role.Author,
    isViewer: (state) => state.role === Role.Viewer,
    isUser: (state) => state.role === Role.User,
    isPAEnabled: (state) => state.hasPA,
    isPEEnabled: (state) => state.hasPE,
    permissions: (state) => state.rolePermissions,
    userEmployerId: (state) => state.employerId,
    // Employer admins may only edit profiles of their own employer, never global profiles
    canEditProfile: (state) => (profileEmployerId?: string | null): boolean =>
      state.role !== Role.EmployerAdmin || (!!profileEmployerId && profileEmployerId === state.employerId),
    roleEmployerIdQuery: (state) =>
      state.role === Role.EmployerAdmin || state.role === Role.Viewer || state.role === Role.TripleaReadonlyAdmin
        ? `&employerId=${state.employerId}`
        : ''
  },

  actions: {
    setUser(user: User) {
      this.email = user.email ?? null;
      this.principalId = user.principalId ?? null;
      this.id = user.id ?? null;
      this.name = user.name ?? null;
      this.role = user.role ?? null;
      this.rolePermissions = user.rolePermissions ?? null;
      this.employerId = user.employerId ?? null;
      this.employerName = user.employerName ?? null;
      this.acceptedPrivacyPolicy = user.acceptedPrivacyPolicy;
      this.acceptedTermsAndConditions = user.acceptedTermsAndConditions;
      this.locale = user.locale;
      this.hasPA = user.hasPA;
      this.hasPE = user.hasPE;
      this.identityProviderType = user.identityProviderType;
    },

    clearUser() {
      this.email = null;
      this.principalId = null;
      this.id = null;
      this.name = null;
      this.role = null;
      this.rolePermissions = null;
      this.employerId = null;
      this.acceptedPrivacyPolicy = null;
      this.acceptedTermsAndConditions = null;
      this.identityProviderType = IdentityProvider.UNKNOWN;
    },

    setLocale(locale?: string): void {
      this.locale = locale;
    },
  },

  persist: true,
});
